import { fromJS } from 'immutable';

/**
 * Remove product from a specific segment of passenger products
 * Helper function to avoid code duplication
 *
 * @param {Map} passengerProducts - Immutable Map of passenger products
 * @param {string} segment - Segment to remove product from ('outgoing' or 'incoming')
 * @param {string} productCode - Product code to remove
 * @returns {Map} Updated passenger products with product removed from segment
 */
export function removeProductFromSegment(passengerProducts, segment, productCode) {
  const segmentProducts = passengerProducts.get(segment);
  const updatedSegmentProducts =
    segmentProducts && segmentProducts.size > 0
      ? segmentProducts.filter((item) => item.get('code') !== productCode)
      : fromJS([]);

  return passengerProducts.set(segment, updatedSegmentProducts);
}

/**
 * Remove product from both segments of passenger products
 * Convenience function that processes both outgoing and incoming segments
 *
 * @param {Map} passengerProducts - Immutable Map of passenger products
 * @param {string} productCode - Product code to remove
 * @returns {Map} Updated passenger products with product removed from both segments
 */
export function removeProductFromAllSegments(passengerProducts, productCode) {
  const updatedOutgoing = removeProductFromSegment(passengerProducts, 'outgoing', productCode);
  return removeProductFromSegment(updatedOutgoing, 'incoming', productCode);
}

/**
 * Update product stock to 0 in catalog segment
 * Helper function to avoid code duplication
 *
 * @param {Map} catalog - Immutable Map of catalog
 * @param {string} segment - Segment to update ('outgoing' or 'incoming')
 * @param {string} productCode - Product code to update
 * @returns {Map} Updated catalog with product stock set to 0
 */
export function setProductStockToZero(catalog, segment, productCode) {
  const segmentCatalog = catalog.get(segment);
  if (!segmentCatalog || segmentCatalog.size === 0) {
    return catalog;
  }

  return catalog.set(
    segment,
    segmentCatalog.map((product) => {
      if (product.get('code') === productCode) {
        return product.set('stock', 0);
      }
      return product;
    }),
  );
}

/**
 * Update product stock to 0 in both catalog segments
 * Convenience function that processes both outgoing and incoming segments
 *
 * @param {Map} catalog - Immutable Map of catalog
 * @param {string} productCode - Product code to update
 * @returns {Map} Updated catalog with product stock set to 0 in both segments
 */
export function setProductStockToZeroInAllSegments(catalog, productCode) {
  const updatedOutgoing = setProductStockToZero(catalog, 'outgoing', productCode);
  return setProductStockToZero(updatedOutgoing, 'incoming', productCode);
}
